import React, { useEffect, useRef, useState } from 'react';
import { AppState, Platform, Pressable, StyleSheet, View } from 'react-native';
import { useAuthStore, useModalStore } from '@/store';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import { PLUS_MONTHLY_CREDITS_DISPLAY, PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY } from '@/constants/config';
import { formatCreditBalance } from '@/lib/formatCredits';
import { formatCreditResetDate, getPlusCreditOptions } from '@/lib/plusCreditOptions';
import { workCreditRecoveryState } from '@/lib/workCredits';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';

export function WorkCreditReset({ user }) {
  const { colors } = useTheme();
  const options = getPlusCreditOptions(user);
  if (!options.isPlus) return null;
  const date = formatCreditResetDate(options.resetDate);
  return <View style={styles.reset}>
    <Icon name="calendar" size={13} color={colors.text_color_secondary} />
    <Text size="xs" color="secondary" style={{ flex: 1 }}>
      {date ? `${formatCreditBalance(options.monthlyTotal)} ${options.isPlusPremium ? 'Plus Premium' : 'Plus'} credits reset on ${date}.`
        : `Your plan includes ${formatCreditBalance(options.monthlyTotal)} credits each month. The next reset date is temporarily unavailable.`}
    </Text>
  </View>;
}

export default function WorkCreditRecoveryCard({ error, requiredCredits, onResume, isResuming = false, mode = 'paused', onClose }) {
  const { theme } = useTheme();
  const user = useAuthStore(s => s.user);
  const { openModal } = useModalStore(s => s.actions);
  const initialBalance = useRef(user?.credits?.totalAvailable ?? 0);
  const [checking, setChecking] = useState(false);
  const [notice, setNotice] = useState('');
  const [balanceChecked, setBalanceChecked] = useState(false);
  const { available, required, ready, reserved: held, shortfall } = workCreditRecoveryState({ error, requiredCredits, balance: user?.credits?.totalAvailable, initialBalance: initialBalance.current, balanceChecked });
  const options = getPlusCreditOptions(user);
  const dark = isDarkTheme(theme);
  const accent = dark ? '#83D9AF' : '#216344';

  // Purchases can complete in another tab or a native store sheet. Refresh on
  // return, and also offer an explicit check when the provider is still settling.
  useEffect(() => {
    const refresh = () => { void useAuthStore.getState().fetchUserCredits({ force: true }); };
    if (Platform.OS === 'web') {
      window.addEventListener('focus', refresh);
      return () => window.removeEventListener('focus', refresh);
    }
    const sub = AppState.addEventListener('change', state => { if (state === 'active') refresh(); });
    return () => sub.remove();
  }, []);

  const checkBalance = async () => {
    if (checking) return;
    setChecking(true);
    const result = await useAuthStore.getState().fetchUserCredits({ force: true });
    setBalanceChecked(!!result);
    setNotice(result ? held ? 'Balance refreshed. After adding credits or when your other tasks finish, try again.' : 'Balance refreshed. If you just paid, your credits may take a moment to arrive.' : 'Couldn’t refresh your balance. Please try again.');
    setChecking(false);
  };
  const buy = () => {
    onClose?.();
    openModal('CreditsPurchaseModal', { initialTab: 'buy-credits', workContext: true, requiredCredits: required, returnToWork: true });
  };
  const upgrade = () => {
    onClose?.();
    openModal('PricingModal', { initialCadence: 'monthly' });
  };
  const title = held ? 'You’re running low on available credits' : ready ? 'You’re ready to keep going' : mode === 'start' ? 'Add credits to bring your idea to life'
    : mode === 'step' ? 'Add credits for the next step'
    : available === 0 ? 'Your work is saved. Add credits to continue.' : 'A few more credits to keep going';

  return <View testID="work-credit-recovery" style={[styles.card, { backgroundColor: dark ? '#101B17' : '#F3F8F5', borderColor: dark ? '#284537' : '#D1E3D8' }]}>
    <View style={styles.heading}>
      <View style={[styles.icon, { backgroundColor: dark ? '#213C2F' : '#E0EEE5' }]}><Icon name={ready ? 'check' : 'coins'} size={18} color={accent} /></View>
      <View style={{ flex: 1, gap: 5 }}>
        <Text size="xs" weight="semibold" style={{ color: accent, letterSpacing: 1 }}>{ready ? 'READY WHEN YOU ARE' : held ? 'CREDITS IN USE' : mode === 'start' ? 'LET’S GET TO WORK' : 'WORK PAUSED · PROGRESS SAVED'}</Text>
        <Text size="m" weight="semibold" style={{ lineHeight: 25 }}>{title}</Text>
      </View>
    </View>
    <Text size="s" color="secondary" style={{ lineHeight: 21 }}>
      {ready ? 'Your balance has updated. Continue when you’re ready.'
        : held ? 'Your other running tasks have reserved some of your credits for their work, leaving too few available for this step. Your progress is saved. Buy more credits to keep going, or try again when those tasks finish.'
        : mode === 'start' ? 'Your request is still in the composer. Top up now, then send it when you’re ready.'
          : mode === 'step' ? 'Your work is saved. Add credits, then choose how you’d like to continue below.'
          : 'Your files and conversation are right here. Add credits, then pick up where you left off.'}
    </Text>
    <View style={[styles.balance, { borderColor: dark ? '#284537' : '#D1E3D8' }]}>
      <Text size="s" weight="semibold">{formatCreditBalance(available)} <Text size="xs" color="secondary">{held ? 'credits in your balance' : 'credits available'}</Text></Text>
      {!!shortfall && <Text size="xs" color="secondary">{formatCreditBalance(shortfall)} more needed for the next step</Text>}
    </View>
    {!!shortfall && <Text size="xs" color="secondary">The next step needs up to {formatCreditBalance(required)} credits available. You’re only charged for what it uses.</Text>}
    <View style={styles.actions}>
      {ready && onResume ? <Button text={isResuming ? 'Resuming…' : 'Resume work'} prependIcon="play" size="medium" variant="purple" onPress={onResume} isLoading={isResuming} isDisabled={isResuming} accessibilityLabel="Resume work" />
        : ready && onClose ? <Button text={mode === 'start' ? 'Back to my request' : 'Back to Work'} size="medium" variant="purple" onPress={onClose} />
          : <Button text="Buy credits" prependIcon="plus" size="medium" variant="purple" onPress={buy} accessibilityLabel="Buy credits to continue Work" />}
      {held && (onResume || onClose) && <Button text={isResuming ? 'Resuming…' : 'Try again'} size="medium" variant="secondary" onPress={onResume || onClose} isLoading={isResuming} isDisabled={isResuming} accessibilityLabel="Try Work again" />}
      {!ready && !held && balanceChecked && available > 0 && (onResume || onClose) && <Button text="Try again" size="medium" variant="secondary" onPress={onResume || onClose} isDisabled={isResuming} />}
      {!ready && !held && (options.canUpgradeToPremium || !options.isPlus) && <Button text={options.isPlus ? 'Explore Plus Premium' : 'Explore Plus'} prependIcon="sparkles" variant="secondary" size="medium" onPress={upgrade} />}
      {!ready && <Pressable accessibilityRole="button" accessibilityLabel="Refresh credit balance" disabled={checking} onPress={checkBalance} style={styles.check}><Text size="xs" color="secondary">{checking ? 'Checking…' : held ? 'Refresh balance' : 'Already added credits?'}</Text></Pressable>}
    </View>
    {!ready && !held && options.canUpgradeToPremium && <Text size="xs" color="secondary">More room for bigger ideas: {PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY} credits every month with Plus Premium.</Text>}
    {!ready && !held && !options.isPlus && <Text size="xs" color="secondary">Plus includes Work and {PLUS_MONTHLY_CREDITS_DISPLAY} credits every month.</Text>}
    <WorkCreditReset user={user} />
    {!!notice && !ready && <View accessibilityLiveRegion="polite"><Text size="xs" color="secondary">{notice}</Text></View>}
  </View>;
}

const styles = StyleSheet.create({
  card: { borderWidth: 1, borderRadius: 18, padding: 20, gap: 13, marginBottom: 10 },
  heading: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  icon: { width: 42, height: 42, borderRadius: 13, alignItems: 'center', justifyContent: 'center' },
  balance: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 6, borderTopWidth: 1, paddingTop: 12 },
  actions: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 10 },
  check: { paddingVertical: 10 },
  reset: { flexDirection: 'row', alignItems: 'center', gap: 8 },
});
